/**
 * Liquid Cooling CDU draft/name/clone bindings for the descriptor engine (#997
 * Story 6.8, mirror of `coolingSystemEngineBindings.ts`).
 *
 * The engine's generic factories (`descriptorCreateDraft` /
 * `descriptorEnsureNameForCreate` / `descriptorCloneForCreate`) don't match the
 * bespoke Liquid Cooling CREATE-flow semantics: bespoke drafts carry
 * `DEFAULT_LIQUID_COOLING_CDU_CONFIGURATION` (status pre-filled to 'new'),
 * auto-names are serial hashes (`generateAutoLiquidCoolingCduName`), and
 * duplicates take a fresh name while dropping id/metadata. Per the story ADR
 * (parity by reuse), each binding here delegates to the PROVEN bespoke util and
 * only converts shapes at the engine boundary (`LiquidCoolingCduConfiguration` ↔
 * `DescriptorInstance` via the Story 6.8 converters).
 */

import {
  cloneLiquidCoolingCduForCreate,
  createDraftLiquidCoolingCduConfiguration,
} from '../../../../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import {
  descriptorInstanceToLiquidCoolingCdu,
  liquidCoolingCduToDescriptorInstance,
} from '../../../../services/descriptorEditSessionFlush';
import { generateAutoLiquidCoolingCduName } from '../../../../utils/liquidCoolingCduAutoName';
import { LIQUID_COOLING_DESCRIPTOR } from '../../../sections/liquidCooling.descriptor';

import type { UpdateLiquidCoolingCduConfigurationInput } from '../../../../types/liquidCoolingCdu';
import type {
  DescriptorInstance,
  DescriptorInstancePatch,
} from '../../../sections/descriptorEngineAdapter';

function patchToLiquidCoolingCduInput(
  data: DescriptorInstancePatch,
): UpdateLiquidCoolingCduConfigurationInput {
  const { instanceName, instanceStatus, ...fields } = data;
  return {
    ...fields,
    name: typeof instanceName === 'string' ? instanceName : undefined,
    status:
      instanceStatus as UpdateLiquidCoolingCduConfigurationInput['status'],
  } as UpdateLiquidCoolingCduConfigurationInput;
}

/** Bespoke draft factory: DEFAULT_LIQUID_COOLING_CDU_CONFIGURATION + normalizeStatus. */
export function liquidCoolingCduCreateDraft(
  data: DescriptorInstancePatch,
  displayOrder: number,
  tempId: number,
): DescriptorInstance {
  const unit = createDraftLiquidCoolingCduConfiguration(
    patchToLiquidCoolingCduInput(data),
    displayOrder,
    tempId,
  );
  return liquidCoolingCduToDescriptorInstance(unit) as DescriptorInstance;
}

/** Bespoke auto-name: serial hash, mirrors ensureLiquidCoolingCduNameForCreate. */
export function liquidCoolingCduEnsureNameForCreate(
  data: DescriptorInstancePatch,
  tempId: number,
  takenNames: Iterable<string | undefined>,
): DescriptorInstancePatch {
  if (typeof data.instanceName === 'string' && data.instanceName.trim()) {
    return data;
  }
  return {
    ...data,
    instanceName: generateAutoLiquidCoolingCduName(tempId, takenNames),
  };
}

/** Bespoke duplicate: fresh name, keeps status, drops id/metadata. */
export function liquidCoolingCduCloneForCreate(
  source: DescriptorInstance,
  takenNames: Iterable<string | undefined>,
  tempId: number,
): Partial<DescriptorInstance> {
  const sourceUnit = descriptorInstanceToLiquidCoolingCdu(
    source,
    0,
    LIQUID_COOLING_DESCRIPTOR,
  );
  const cloned = cloneLiquidCoolingCduForCreate(sourceUnit, takenNames, tempId);
  return liquidCoolingCduToDescriptorInstance({
    ...cloned,
    id: tempId,
  } as Parameters<typeof liquidCoolingCduToDescriptorInstance>[0]) as Partial<DescriptorInstance>;
}
